import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ProductFilters } from "@/types/filters";

const HAIR_TYPES = ["curly", "straight", "wavy", "coily", "fine", "colored", "damaged", "all"];
const FEATURES = ["vegan", "cruelty-free", "natural", "organic", "color-safe", "anti-frizz", "keratin", "volume", "color-protection"];
const CATEGORIES = ["shampoo", "treatment", "mask", "system", "oil", "gel"];

interface FilterBarProps {
  filters: ProductFilters;
  onFilterChange: (filters: ProductFilters) => void;
}

const FilterBar = ({ filters, onFilterChange }: FilterBarProps) => {
  const { t } = useTranslation();

  const handleHairTypeChange = (type: string) => {
    const currentTypes = filters.hairType || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];
    onFilterChange({ ...filters, hairType: newTypes });
  };

  const handleFeatureChange = (feature: string) => {
    const currentFeatures = filters.features || [];
    const newFeatures = currentFeatures.includes(feature)
      ? currentFeatures.filter((f) => f !== feature)
      : [...currentFeatures, feature];
    onFilterChange({ ...filters, features: newFeatures });
  };

  const handleCategoryChange = (category: string) => {
    onFilterChange({ ...filters, category: filters.category === category ? undefined : category });
  };

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            {t('products.filters.hairType')} ({filters.hairType?.length || 0})
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>{t('products.filters.selectHairType')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {HAIR_TYPES.map((type) => (
            <DropdownMenuCheckboxItem
              key={type}
              checked={filters.hairType?.includes(type)}
              onCheckedChange={() => handleHairTypeChange(type)}
            >
              {t(`products.hairTypes.${type}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            {t('products.filters.features')} ({filters.features?.length || 0})
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>{t('products.filters.selectFeatures')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {FEATURES.map((feature) => (
            <DropdownMenuCheckboxItem
              key={feature}
              checked={filters.features?.includes(feature)}
              onCheckedChange={() => handleFeatureChange(feature)}
            >
              {t(`products.features.${feature}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            {filters.category ? t(`products.categories.${filters.category}`) : t('products.filters.category')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>{t('products.filters.selectCategory')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {CATEGORIES.map((category) => (
            <DropdownMenuCheckboxItem
              key={category}
              checked={filters.category === category}
              onCheckedChange={() => handleCategoryChange(category)}
            >
              {t(`products.categories.${category}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default FilterBar;